import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ArrowRightLeft, Loader2, Pencil, Plus, Save, Target, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { formatDate } from "@/lib/constants";

// M17 P6 — Admin-set monthly joining targets per recruiter
function MonthlyTargets() {
  const thisMonth = new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(thisMonth);
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/recruiter-flow/targets", { params: { month } })
      .then((r) => {
        setRows(r.data.items || []);
        setDrafts({});
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [month]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (row) => {
    const raw = drafts[row.recruiter_id];
    const val = Number(raw);
    if (raw === undefined || raw === "" || Number.isNaN(val) || val < 0) return toast.error("Enter a valid target (0 or more)");
    setSavingId(row.recruiter_id);
    try {
      await api.put(`/recruiter-flow/targets/${row.recruiter_id}`, { month, target: val });
      toast.success(`${row.recruiter_name}'s target for ${month} set to ${val}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Card className="rounded-xl border-border shadow-none" data-testid="monthly-targets-card">
      <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" /> Monthly joining targets
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Targets show live on each recruiter's dashboard and profile, with joined count and gap.
          </p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               className="w-44" data-testid="targets-month-input" />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-32 rounded-lg" />
        ) : (
          <div className="rounded-lg border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recruiter</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Joined</TableHead>
                  <TableHead className="text-right">Gap</TableHead>
                  <TableHead className="text-right">Set target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="targets-table-body">
                {rows.map((r) => (
                  <TableRow key={r.recruiter_id} data-testid={`target-row-${r.recruiter_id}`}>
                    <TableCell className="font-medium">
                      {r.recruiter_name}
                      {!r.active && <Badge variant="outline" className="ml-2 rounded-full text-[10px]">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-right font-mono">{r.target || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-700">{r.joined}</TableCell>
                    <TableCell className="text-right font-mono">{r.target ? r.gap : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Input type="number" min="0" placeholder={String(r.target || 0)}
                               value={drafts[r.recruiter_id] ?? ""}
                               onChange={(e) => setDrafts({ ...drafts, [r.recruiter_id]: e.target.value })}
                               className="w-20 h-8 text-right"
                               data-testid={`target-input-${r.recruiter_id}`} />
                        <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white gap-1"
                                onClick={() => save(r)} disabled={savingId === r.recruiter_id}
                                data-testid={`target-save-${r.recruiter_id}`}>
                          {savingId === r.recruiter_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          Save
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminRecruiters() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", mobile: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [deact, setDeact] = useState(null);          // recruiter being deactivated (with active work)
  const [deactWork, setDeactWork] = useState(null);  // active-work summary
  const [target, setTarget] = useState("");          // chosen transfer target id
  const [deactBusy, setDeactBusy] = useState(false);
  const [editRec, setEditRec] = useState(null);      // recruiter being edited (basic details)
  const [editForm, setEditForm] = useState({ name: "", email: "", whatsapp_number: "" });
  const [editBusy, setEditBusy] = useState(false);

  const load = useCallback(() => {
    api
      .get("/users/recruiters")
      .then((res) => setRecruiters(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (form.name.trim().length < 2) return toast.error("Name is required");
    if (!/^[0-9]{10}$/.test(form.mobile)) return toast.error("Enter a valid 10-digit mobile number");
    setBusy(true);
    try {
      await api.post("/users/recruiters", { name: form.name.trim(), mobile: form.mobile, email: form.email || null });
      toast.success("Recruiter created — they can now log in with OTP");
      setDialogOpen(false);
      setForm({ name: "", mobile: "", email: "" });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (r) => {
    setEditForm({ name: r.name || "", email: r.email || "", whatsapp_number: r.whatsapp_number || "" });
    setEditRec(r);
  };

  const submitEdit = async () => {
    if (editForm.name.trim().length < 2) return toast.error("Name is required");
    setEditBusy(true);
    try {
      await api.put(`/users/recruiters/${editRec.id}`, {
        name: editForm.name.trim(),
        email: editForm.email.trim() || null,
        whatsapp_number: editForm.whatsapp_number.trim() || null,
      });
      toast.success(`${editForm.name.trim()}'s details updated`);
      setEditRec(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setEditBusy(false);
    }
  };

  const toggleActive = async (recruiter) => {
    // Activating: simple flip.
    if (!recruiter.active) {
      try {
        await api.put(`/users/recruiters/${recruiter.id}`, { active: true });
        toast.success(`${recruiter.name} activated`);
        load();
      } catch (err) {
        toast.error(getErrorMessage(err));
      }
      return;
    }
    // Deactivating: check active work first.
    try {
      const { data } = await api.get(`/users/recruiters/${recruiter.id}/active-work`);
      if (data.active_work.total === 0) {
        await api.post(`/users/recruiters/${recruiter.id}/deactivate`, {});
        toast.success(`${recruiter.name} deactivated`);
        load();
      } else {
        setDeactWork(data.active_work);
        setTarget("");
        setDeact(recruiter);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const confirmDeactivate = async () => {
    if (!target) return toast.error("Choose a recruiter to transfer the work to");
    setDeactBusy(true);
    try {
      const { data } = await api.post(`/users/recruiters/${deact.id}/deactivate`, { target_recruiter_id: target });
      const t = data.transferred;
      toast.success(`${deact.name} deactivated — ${t.total} record(s) transferred`);
      setDeact(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDeactBusy(false);
    }
  };

  const WORK_LABELS = {
    applications: "Active applications (incl. Call Backs)",
    candidates: "Candidate ownership",
    scheduled_interviews: "Scheduled interviews",
    selection_records: "Status Pending / Selection",
    employments: "Joined / tracked placements",
  };

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-64 rounded-lg" /><Skeleton className="h-64 rounded-xl" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Recruiters</h1>
          <p className="text-muted-foreground mt-1 text-sm">Manage recruiter accounts. Recruiters log in with mobile + OTP.</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="rounded-lg" data-testid="admin-recruiter-create-button">
          <Plus className="h-4 w-4 mr-2" /> Add recruiter
        </Button>
      </div>

      {recruiters.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={Users} title="No recruiters yet" description="Add recruiters so you can assign applications to them." actionLabel="Add recruiter" onAction={() => setDialogOpen(true)} testId="admin-recruiters-empty" />
        </Card>
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="admin-recruiters-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Recruiter</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead>Active pipeline</TableHead>
                  <TableHead>Total handled</TableHead>
                  <TableHead>Joined</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-center">Edit</TableHead>
                  <TableHead className="text-right">Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recruiters.map((r) => (
                  <TableRow key={r.id} className="hover:bg-muted/60" data-testid="admin-recruiter-row">
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                          {r.name.charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <p className="font-medium">{r.name}</p>
                          {r.email && <p className="text-xs text-muted-foreground">{r.email}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{r.mobile}</TableCell>
                    <TableCell className="font-mono text-sm">{r.active_assignments}</TableCell>
                    <TableCell className="font-mono text-sm">{r.total_assignments}</TableCell>
                    <TableCell className="text-sm">{formatDate(r.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`rounded-full text-xs ${r.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                        {r.active ? "Active" : "Deactivated"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-muted-foreground hover:text-foreground"
                              onClick={() => openEdit(r)} data-testid={`recruiter-edit-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </Button>
                    </TableCell>
                    <TableCell className="text-right">
                      <Switch checked={r.active} onCheckedChange={() => toggleActive(r)} data-testid={`recruiter-toggle-${r.id}`} aria-label="Toggle recruiter active status" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <MonthlyTargets />

      {/* Edit basic details — reuses the existing admin update endpoint. Does not
          touch pipeline, history, or active status. Mobile is the login ID (read-only). */}
      <Dialog open={!!editRec} onOpenChange={(o) => !o && setEditRec(null)}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="recruiter-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit recruiter details</DialogTitle>
            <DialogDescription>Update basic details. This does not change their pipeline, history, or active status.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={editForm.name} data-testid="recruiter-edit-name"
                     onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Recruiter name" />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile (login ID — cannot be changed)</Label>
              <Input value={editRec?.mobile || ""} disabled data-testid="recruiter-edit-mobile"
                     className="font-mono bg-muted/50" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} data-testid="recruiter-edit-email"
                     onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@example.com" />
            </div>
            <div className="space-y-1.5">
              <Label>WhatsApp number</Label>
              <Input value={editForm.whatsapp_number} data-testid="recruiter-edit-whatsapp"
                     onChange={(e) => setEditForm((f) => ({ ...f, whatsapp_number: e.target.value }))} placeholder="Optional" />
            </div>
            <Button onClick={submitEdit} disabled={editBusy} className="w-full rounded-lg" data-testid="recruiter-edit-save">
              {editBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer-then-deactivate prompt (only shown when the recruiter owns active work) */}
      <Dialog open={!!deact} onOpenChange={(o) => !o && setDeact(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="recruiter-deactivate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" /> Transfer work before deactivating
            </DialogTitle>
            <DialogDescription>
              {deact?.name} still owns active work. Choose a recruiter to receive all of it. Closed/historical
              records and company allocations are not affected.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {deactWork && (
              <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1" data-testid="deactivate-work-summary">
                {Object.entries(WORK_LABELS).filter(([k]) => deactWork[k] > 0).map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{label}</span>
                    <span className="font-mono font-medium">{deactWork[k]}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-sm pt-1 border-t border-border mt-1">
                  <span className="font-medium">Total to transfer</span>
                  <span className="font-mono font-semibold">{deactWork.total}</span>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Transfer all of it to</Label>
              <div className="space-y-1.5 max-h-52 overflow-y-auto" data-testid="deactivate-target-list">
                {recruiters.filter((r) => r.active && r.id !== deact?.id).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No other active recruiter available. Add or activate one first.</p>
                ) : recruiters.filter((r) => r.active && r.id !== deact?.id).map((r) => (
                  <button key={r.id} type="button" onClick={() => setTarget(r.id)}
                    data-testid={`deactivate-target-${r.id}`}
                    className={`w-full flex items-center gap-2.5 rounded-lg border p-2.5 text-left transition-colors ${
                      target === r.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                    }`}>
                    <span className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold">
                      {r.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="text-sm font-medium flex-1">{r.name}</span>
                    {target === r.id && <ArrowRightLeft className="h-4 w-4 text-primary" />}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={confirmDeactivate} disabled={deactBusy || !target}
              className="w-full rounded-lg" data-testid="deactivate-confirm-button">
              {deactBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Transfer &amp; deactivate
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-sm rounded-xl" data-testid="recruiter-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Add recruiter</DialogTitle>
            <DialogDescription>The recruiter will log in using their mobile number with OTP.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full name *</Label>
              <Input data-testid="recruiter-form-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Mobile number *</Label>
              <Input data-testid="recruiter-form-mobile" inputMode="numeric" maxLength={10} value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="recruiter-form-email" type="email" placeholder="optional" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <Button onClick={submit} disabled={busy} className="w-full rounded-lg" data-testid="recruiter-form-submit">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create recruiter
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
